import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Divider,
  Box,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Button,
  Chip,
  ButtonGroup,
  Tooltip,
  IconButton,
  Autocomplete,
  TextField,
  TableFooter
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { tr } from 'date-fns/locale';
import { format, parseISO, isValid } from 'date-fns';
import { utils, writeFile } from 'xlsx';
import { accountingApi } from '../../../../config/apiClient';
import { useTranslation } from 'react-i18next';

interface StockReportTabProps {
  companyId: string | null;
  screenId: string | null;
}

interface StockMovementItem {
  id: string;
  itemId: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  totalAmount: number;
  date: string; // YYYY-MM-DD formatında
  createdAt?: string; // PosReportTab'a benzerlik için eklendi, tarih sıralaması için kullanılabilir
  description?: string;
  movementType?: string;
  createdBy?: string; // Kasiyer bilgisi için eklendi
}

const formatDateToDDMMYYYY = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, 'dd.MM.yyyy', { locale: tr });
    }
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)){
        const parts = dateString.split('-');
        const parsedDate = new Date(parseInt(parts[0]), parseInt(parts[1]) -1, parseInt(parts[2]));
        if(isValid(parsedDate)){
            return format(parsedDate, 'dd.MM.yyyy', { locale: tr });
        }
    }
    return '-';
  } catch (error) {
    return '-';
  }
};

const formatCurrency = (amount: number, currencyCode: string = 'TRY') => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2
  }).format(amount);
};

const StockReportTab: React.FC<StockReportTabProps> = ({ companyId, screenId }) => {
  const { t } = useTranslation('salesSystem');
  const [stockMovements, setStockMovements] = useState<StockMovementItem[]>([]);
  const [filteredMovements, setFilteredMovements] = useState<StockMovementItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const [dateFilter, setDateFilter] = useState<'today' | 'week' | 'month' | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [productNameFilter, setProductNameFilter] = useState<string>('');
  const [selectedCashier, setSelectedCashier] = useState<string | null>(null);

  const reportTitle = t('stockReportTab.title');

  const formatDateForFirebase = (date: Date | null): string | null => {
    return date ? format(date, 'yyyy-MM-dd') : null;
  };

  const fetchStockMovements = useCallback(async () => {
    if (!companyId || !screenId) {
      setStockMovements([]);
      setFilteredMovements([]);
      setSelectedCashier(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      console.log('📋 [STOK RAPORU] SQL\'den stok hareketleri yükleniyor...', { companyId, screenId });
      
      const response = await accountingApi.get(`/api/StockMovement`);
      const result = response.data;
      const sqlMovements = result.data || result;
      
      console.log(`✅ [STOK RAPORU] SQL'den ${sqlMovements.length} stok hareketi yüklendi`);
      
      if (!sqlMovements || sqlMovements.length === 0) {
        setStockMovements([]);
        setLoading(false);
        return;
      }
      
      // SQL verilerini frontend formatına çevir ve filtrele
      const loadedMovements: StockMovementItem[] = sqlMovements
        .filter((movement: any) => {
          // 1. Bu ekrana ait olmalı
          const matchesScreen = movement.screenId?.toString() === screenId?.toString();
          // 2. Hızlı satış hareketi olmalı (MovementType: QUICK_SALE veya quick_sale)
          const isQuickSale = movement.movementType?.toUpperCase() === 'QUICK_SALE';
          
          console.log(`🔍 [STOK RAPORU] Hareket ${movement.id}: screenId=${movement.screenId}, movementType=${movement.movementType}, matchesScreen=${matchesScreen}, isQuickSale=${isQuickSale}`);
          
          return matchesScreen && isQuickSale;
        })
        .map((movement: any) => ({
          id: movement.id.toString(),
          itemId: movement.productId || '',
          itemName: movement.productName || t('stockReportTab.common.unknownProduct'),
          quantity: parseFloat(movement.quantity) || 0,
          unitPrice: parseFloat(movement.unitPrice) || 0,
          totalAmount: parseFloat(movement.totalAmount) || 0,
          date: movement.date || movement.createdAt,
          createdAt: movement.createdAt || movement.date,
          description: movement.description,
          movementType: movement.movementType,
          createdBy: movement.createdBy || t('stockReportTab.common.unknown')
        }))
        .sort((a: StockMovementItem, b: StockMovementItem) => {
          const dateA = a.createdAt || a.date;
          const dateB = b.createdAt || b.date;
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return new Date(dateB).getTime() - new Date(dateA).getTime();
        });
      
      console.log(`✅ [STOK RAPORU] ${loadedMovements.length} hareket (ekran ID: ${screenId}) işlendi ve sıralandı`);
      setStockMovements(loadedMovements);
    } catch (err) {
      console.error("❌ [STOK RAPORU] Stok hareketleri çekilirken hata:", err);
      setError(t('stockReportTab.errors.loadFailed'));
      setStockMovements([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, screenId]);

  useEffect(() => {
    fetchStockMovements();
  }, [fetchStockMovements]);

  useEffect(() => {
    let newFilteredMovements = [...stockMovements];
    if (dateFilter) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let filterStartDate: Date;
      switch (dateFilter) {
        case 'today':
          filterStartDate = today;
          const endOfToday = new Date(today);
          endOfToday.setHours(23, 59, 59, 999);
          newFilteredMovements = newFilteredMovements.filter(m => {
            const dateValue = m.createdAt || m.date;
            if (!dateValue) return false;
            const transactionDate = m.createdAt ? parseISO(dateValue) : new Date(dateValue);
            return isValid(transactionDate) && transactionDate >= filterStartDate && transactionDate <= endOfToday;
          });
          break;
        case 'week':
          filterStartDate = new Date(today);
          filterStartDate.setDate(today.getDate() - 7);
          newFilteredMovements = newFilteredMovements.filter(m => {
            const dateValue = m.createdAt || m.date;
            if (!dateValue) return false;
            const transactionDate = m.createdAt ? parseISO(dateValue) : new Date(dateValue);
            return isValid(transactionDate) && transactionDate >= filterStartDate;
          });
          break;
        case 'month':
          filterStartDate = new Date(today);
          filterStartDate.setMonth(today.getMonth() - 1);
          newFilteredMovements = newFilteredMovements.filter(m => {
            const dateValue = m.createdAt || m.date;
            if (!dateValue) return false;
            const transactionDate = m.createdAt ? parseISO(dateValue) : new Date(dateValue);
            return isValid(transactionDate) && transactionDate >= filterStartDate;
          });
          break;
      }
    } else if (startDate || endDate) {
      const sDate = startDate ? new Date(new Date(startDate).setHours(0, 0, 0, 0)) : null;
      const eDate = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : null;
      newFilteredMovements = newFilteredMovements.filter(m => {
        const dateValue = m.createdAt || m.date;
        if (!dateValue) return false;
        const transactionDate = m.createdAt ? parseISO(dateValue) : new Date(dateValue);
        if (!isValid(transactionDate)) return false;

        const afterStartDate = sDate ? transactionDate >= sDate : true;
        const beforeEndDate = eDate ? transactionDate <= eDate : true;
        return afterStartDate && beforeEndDate;
      });
    }

    if (productNameFilter.trim() !== '') {
      newFilteredMovements = newFilteredMovements.filter(m =>
        m.itemName.toLowerCase().includes(productNameFilter.trim().toLowerCase())
      );
    }

    if (selectedCashier) {
      newFilteredMovements = newFilteredMovements.filter(m => m.createdBy === selectedCashier);
    }

    setFilteredMovements(newFilteredMovements);
    setPage(0);
  }, [stockMovements, dateFilter, startDate, endDate, productNameFilter, selectedCashier]);

  const totalFilteredQuantity = useMemo(() => {
    return filteredMovements.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [filteredMovements]);

  // Tablo için genel toplamlar
  const overallStockQuantity = useMemo(() => {
    return filteredMovements.reduce((sum, item) => sum + (item.quantity || 0), 0);
  }, [filteredMovements]);

  const overallStockTotalAmount = useMemo(() => {
    return filteredMovements.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
  }, [filteredMovements]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getDateFilterLabel = (): string => {
    if (dateFilter) {
      switch (dateFilter) {
        case 'today': return t('stockReportTab.dateFilters.today');
        case 'week': return t('stockReportTab.dateFilters.lastWeek');
        case 'month': return t('stockReportTab.dateFilters.lastMonth');
        default: return '';
      }
    } else if (startDate && endDate) {
      return t('stockReportTab.dateFilters.range', {
        start: startDate.toLocaleDateString(tr.code),
        end: endDate.toLocaleDateString(tr.code),
      });
    } else if (startDate) {
      return t('stockReportTab.dateFilters.startOnly', { start: startDate.toLocaleDateString(tr.code) });
    } else if (endDate) {
      return t('stockReportTab.dateFilters.endOnly', { end: endDate.toLocaleDateString(tr.code) });
    }
    return '';
  };

  const uniqueProductNames = useMemo(() => {
    const names = new Set(stockMovements.map(item => item.itemName));
    return Array.from(names).sort();
  }, [stockMovements]);

  const uniqueCashiers = useMemo(() => {
    if (stockMovements.length === 0) return [];
    const cashierSet = new Set(stockMovements.map(m => m.createdBy || t('stockReportTab.common.unknown')));
    return Array.from(cashierSet).sort();
  }, [stockMovements]);

  // Excel indirme fonksiyonu
  const exportToExcel = () => {
    if (filteredMovements.length === 0) return;
    
    // Excel verilerini hazırla
    const excelData = filteredMovements.map(item => {
      return {
        [t('stockReportTab.table.date')]: formatDateToDDMMYYYY(item.createdAt || item.date),
        [t('stockReportTab.table.productName')]: item.itemName || '',
        [t('stockReportTab.table.quantity')]: item.quantity || 0,
        [t('stockReportTab.table.unitPrice')]: formatCurrency(item.unitPrice),
        [t('stockReportTab.table.totalAmount')]: formatCurrency(item.totalAmount),
        [t('stockReportTab.table.cashier')]: item.createdBy || ''
      };
    });

    // Excel çalışma kitabı oluştur
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(excelData);

    // Sütun genişliklerini ayarla
    const colWidths = [
      { wch: 15 }, // Tarih
      { wch: 40 }, // Ürün Adı
      { wch: 10 }, // Miktar
      { wch: 15 }, // Birim Fiyat
      { wch: 15 }, // Toplam Tutar
      { wch: 15 }  // Kasiyer
    ];
    ws['!cols'] = colWidths;

    // Çalışma sayfasını kitaba ekle
    utils.book_append_sheet(wb, ws, t('stockReportTab.title'));

    // Dosya adını hazırla
    const fileName = `Stock_Report_${new Date().toLocaleDateString('tr-TR').replace(/\./g, '-')}.xlsx`;

    // Excel dosyasını indir
    writeFile(wb, fileName);
  };

  if (!companyId || !screenId) {
    return <Alert severity="warning">{t('stockReportTab.errors.companyOrScreenIdMissing')}</Alert>;
  }

  return (
    <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mr: 1 }}>{reportTitle}</Typography>
            {filteredMovements.length > 0 && (
              <Chip
                label={t('stockReportTab.summary.totalSoldQuantity', { count: totalFilteredQuantity })}
                color="success"
                variant="filled"
                size="small"
                sx={{ fontWeight: 'bold', fontSize: '0.9rem', padding: '4px 8px', mr: 1, mb: 0.5 }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' }, flexGrow: 1 }}>
            <Autocomplete
              options={uniqueProductNames}
              value={productNameFilter || null}
              onChange={(event, newValue) => {
                setProductNameFilter(newValue || '');
              }}
              onInputChange={(event, newInputValue) => {
                // Kullanıcı metin kutusuna yazarken de filtreleme yapabiliriz
                // Ancak `onChange` ile seçimi kesinleştirmek daha iyi olabilir.
                // Şimdilik boş bırakıyorum, gerekirse `setProductNameFilter(newInputValue || '');` eklenebilir.
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('stockReportTab.filters.searchProductName')}
                  size="small"
                  variant="outlined"
                  sx={{ width: { xs: '100%', sm: 200 }, mr: 1 }}
                />
              )}
              sx={{ mb: { xs: 1, sm: 0 } }}
            />

            {uniqueCashiers.length > 0 && (
              <Autocomplete
                options={uniqueCashiers}
                value={selectedCashier}
                onChange={(event, newValue) => {
                  setSelectedCashier(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('stockReportTab.filters.selectCashier')}
                    size="small"
                    variant="outlined"
                    sx={{ width: { xs: '100%', sm: 180 }, mr: 1 }}
                  />
                )}
                sx={{ mb: { xs: 1, sm: 0 } }}
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
                <DatePicker
                  label={t('stockReportTab.filters.startDate')}
                  value={startDate}
                  onChange={(newValue) => { setStartDate(newValue); setDateFilter(null); }}
                  maxDate={endDate || undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: 'calc(50% - 4px)', sm: 150 } } } }}
                />
                <DatePicker
                  label={t('stockReportTab.filters.endDate')}
                  value={endDate}
                  onChange={(newValue) => { setEndDate(newValue); setDateFilter(null); }}
                  minDate={startDate || undefined}
                  slotProps={{ textField: { size: 'small', sx: { width: { xs: 'calc(50% - 4px)', sm: 150 } } } }}
                />
              </LocalizationProvider>
              {(startDate || endDate || productNameFilter || selectedCashier) && (
                <Tooltip title={t('stockReportTab.actions.clearAllFilters')}>
                  <IconButton onClick={() => { setStartDate(null); setEndDate(null); setDateFilter(null); setProductNameFilter(''); setSelectedCashier(null); }} size="small">
                    <FilterAltOffIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {dateFilter && (
                <Chip
                  icon={<FilterAltIcon />}
                  label={getDateFilterLabel()}
                  variant="filled"
                  color="primary"
                  size="small"
                  onDelete={() => setDateFilter(null)}
                  deleteIcon={<FilterAltOffIcon />}
                  sx={{ mr: 1.5 }}
                />
              )}
              <ButtonGroup size="small" variant="outlined">
                <Tooltip title={t('stockReportTab.tooltips.todayRecords')}>
                  <Button onClick={() => { setDateFilter('today'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'today' ? 'primary' : 'inherit'} variant={dateFilter === 'today' ? 'contained' : 'outlined'} startIcon={<CalendarTodayIcon />}>{t('stockReportTab.dateFilters.today')}</Button>
                </Tooltip>
                <Tooltip title={t('stockReportTab.tooltips.last7DaysRecords')}>
                  <Button onClick={() => { setDateFilter('week'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'week' ? 'primary' : 'inherit'} variant={dateFilter === 'week' ? 'contained' : 'outlined'} startIcon={<DateRangeIcon />}>{t('stockReportTab.dateFilters.lastWeek')}</Button>
                </Tooltip>
                <Tooltip title={t('stockReportTab.tooltips.last30DaysRecords')}>
                  <Button onClick={() => { setDateFilter('month'); setStartDate(null); setEndDate(null); }} color={dateFilter === 'month' ? 'primary' : 'inherit'} variant={dateFilter === 'month' ? 'contained' : 'outlined'} startIcon={<EventNoteIcon />}>{t('stockReportTab.dateFilters.lastMonth')}</Button>
                </Tooltip>
              </ButtonGroup>
              
              {/* Excel İndirme Butonu */}
              <Button
                variant="contained"
                color="info"
                startIcon={<DownloadIcon />}
                onClick={exportToExcel}
                disabled={filteredMovements.length === 0}
                sx={{ ml: 2 }}
              >
                {t('stockReportTab.actions.downloadExcel')}
              </Button>
            </Box>
          </Box>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}
      {!loading && !error && filteredMovements.length === 0 && (
        <Typography variant="body1" align="center" sx={{ py: 4 }}>
          {dateFilter || startDate || endDate
            ? t('stockReportTab.emptyState.noRecordsInDateRange')
            : t('stockReportTab.emptyState.noRecordsForScreen')}
        </Typography>
      )}

      {!loading && !error && filteredMovements.length > 0 && (
        <>
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%' }}>{t('stockReportTab.table.date')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '30%' }}>{t('stockReportTab.table.productName')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%', textAlign: 'right' }}>{t('stockReportTab.table.quantity')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%', textAlign: 'right' }}>{t('stockReportTab.table.unitPrice')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '15%', textAlign: 'right' }}>{t('stockReportTab.table.totalAmount')}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', bgcolor: 'secondary.main', color: 'white', width: '10%' }}>{t('stockReportTab.table.cashier')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMovements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                  <TableRow 
                    key={item.id} 
                    hover 
                    sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                  >
                    <TableCell sx={{ fontSize: '0.875rem' }}>{formatDateToDDMMYYYY(item.createdAt || item.date)}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{item.itemName}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', textAlign: 'right' }}>{item.quantity}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', textAlign: 'right' }}>{formatCurrency(item.unitPrice)}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', textAlign: 'right', fontWeight: 'medium' }}>{formatCurrency(item.totalAmount)}</TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>{item.createdBy}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow sx={{ '& td': { fontWeight: 'bold', fontSize: '0.9rem' } }}>
                  <TableCell>{t('stockReportTab.summary.grandTotal')}</TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{overallStockQuantity}</TableCell>
                  <TableCell></TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatCurrency(overallStockTotalAmount)}</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredMovements.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('stockReportTab.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => t('stockReportTab.pagination.displayedRows', { count, from, to })}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </>
      )}
    </Paper>
  );
};

export default StockReportTab; 